import { RecordDefinition } from "./recordDefinition.types";
import { registerFieldComponents } from "../config/fieldComponents";
import { registerFieldValidators } from "../config/fieldValidators";
import { registerFieldBehaviours } from "../config/fieldBehaviours";
import { registerFieldMinDates } from "../config/fieldMinDates";
import { registerLookupOptionFilters } from "../config/lookupOptionFilters";
import { registerVisibilityRules } from "../config/visibilityRules";
import { abtestRecordDefinition } from "./abtest/abtestRecordDefinition";
import { accountRecordDefinition } from "./account/accountRecordDefinition";
import { automationRecordDefinition } from "./automation/automationRecordDefinition";
import { campaignRecordDefinition } from "./campaign/campaignRecordDefinition";
import { communicationActivityRecordDefinition } from "./communicationActivity/communicationActivityRecordDefinition";
import { communicationAudienceRecordDefinition } from "./communicationAudience/communicationAudienceRecordDefinition";
import { communicationRecordDefinition } from "./communication/communicationRecordDefinition";
import { communicationAuditRecordDefinition } from "./communicationAudit/communicationAuditRecordDefinition";
import { communicationSettingsRecordDefinition } from "./communicationSettings/communicationSettingsRecordDefinition";
import { competitionRecordDefinition } from "./competition/competitionRecordDefinition";
import { competitionStageRecordDefinition } from "./competitionStage/competitionStageRecordDefinition";
import { contactRecordDefinition } from "./contact/contactRecordDefinition";
import { contactAuditRecordDefinition } from "./contactAudit/contactAuditRecordDefinition";
import { dataModelRecordDefinition } from "./dataModel/dataModelRecordDefinition";
import { eventProfileRecordDefinition } from "./eventProfile/eventProfileRecordDefinition";
import { fixtureRecordDefinition } from "./fixture/fixtureRecordDefinition";
import { fixtureGradeRecordDefinition } from "./fixtureGrade/fixtureGradeRecordDefinition";
import { opponentRecordDefinition } from "./opponent/opponentRecordDefinition";
import { opponentTypeRecordDefinition } from "./opponentType/opponentTypeRecordDefinition";
import { quickStartRecordDefinition } from "./quickStart/quickStartRecordDefinition";
import { marketingListRecordDefinition } from "./marketingList/marketingListRecordDefinition";
import { subscriptionGroupRecordDefinition } from "./subscriptionGroup/subscriptionGroupRecordDefinition";
import { seasonRecordDefinition } from "./season/seasonRecordDefinition";
import { seasonalPeriodRecordDefinition } from "./seasonalPeriod/seasonalPeriodRecordDefinition";
import { sitemapRecordDefinition } from "./sitemap/sitemapRecordDefinition";
import { teamRecordDefinition } from "./team/teamRecordDefinition";
import { ticketHospitalityProductRecordDefinition } from "./ticketHospitalityProduct/ticketHospitalityProductRecordDefinition";
import { typeRecordDefinition } from "./type/typeRecordDefinition";
import { venueStatusRecordDefinition } from "./venueStatus/venueStatusRecordDefinition";
import { warehouseApplicationRecordDefinition } from "./warehouseApplication/warehouseApplicationRecordDefinition";
import { warehouseSettingsRecordDefinition } from "./warehouseSettings/warehouseSettingsRecordDefinition";

/**
 * Every page the page builder serves, and everything that makes it one page.
 * Adding a page is its definition and config in its own folder, plus an entry
 * here - no per-page components.
 */
export const recordDefinitions: RecordDefinition[] = [
  abtestRecordDefinition,
  accountRecordDefinition,
  automationRecordDefinition,
  campaignRecordDefinition,
  communicationActivityRecordDefinition,
  communicationAudienceRecordDefinition,
  communicationRecordDefinition,
  communicationAuditRecordDefinition,
  communicationSettingsRecordDefinition,
  competitionRecordDefinition,
  competitionStageRecordDefinition,
  contactRecordDefinition,
  contactAuditRecordDefinition,
  dataModelRecordDefinition,
  eventProfileRecordDefinition,
  fixtureRecordDefinition,
  fixtureGradeRecordDefinition,
  marketingListRecordDefinition,
  opponentRecordDefinition,
  opponentTypeRecordDefinition,
  quickStartRecordDefinition,
  seasonRecordDefinition,
  seasonalPeriodRecordDefinition,
  sitemapRecordDefinition,
  subscriptionGroupRecordDefinition,
  teamRecordDefinition,
  ticketHospitalityProductRecordDefinition,
  typeRecordDefinition,
  venueStatusRecordDefinition,
  warehouseApplicationRecordDefinition,
  warehouseSettingsRecordDefinition,
];

// One registry for every page: a related table on one page can name a component
// another page supplies, and the renderers below src/page/pages cannot import this.
recordDefinitions.forEach((definition) => {
  registerFieldComponents(definition.fieldComponents);
  registerFieldValidators(definition.fieldValidators);
  registerFieldBehaviours(definition.fieldBehaviours);
  registerFieldMinDates(definition.fieldMinDates);
  registerLookupOptionFilters(definition.lookupOptionFilters);
  registerVisibilityRules(definition.visibilityRules);
});

const byEntityName = new Map(
  recordDefinitions.map((definition) => [definition.entityName, definition])
);

const byLowerCaseEntityName = new Map(
  recordDefinitions.map((definition) => [
    definition.entityName.toLowerCase(),
    definition,
  ])
);

const byPath = new Map(
  recordDefinitions.map((definition) => [definition.path, definition])
);

export function getRecordDefinition(entityName: string) {
  return byEntityName.get(entityName);
}

/** For an entity name held in data, whose casing is not the definition's own. */
export function getRecordDefinitionIgnoringCase(entityName: string) {
  return byLowerCaseEntityName.get(entityName.toLowerCase());
}

/** Looked up by the page's own route segment, so the designer's url reads like the page. */
export function getRecordDefinitionByPath(path: string | undefined) {
  return path ? byPath.get(`/${path}`) : undefined;
}
